import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const WD = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const BLANK = { name: "", mode: "flex", start: "08:00", end: "17:00", weekdays: [0, 1, 2, 3, 4], shifts: [{ start: "08:00", end: "12:00" }, { start: "13:00", end: "17:00" }] };

export const typeLabel = (t) => t.mode === "recur"
  ? `ripetitivo · ${t.weekdays.map((d) => WD[d]).join(" ")} · ${t.shifts.map((s) => `${s.start}-${s.end}`).join(", ")}`
  : `flessibile · ${t.start}-${t.end}`;

function NewTypeDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(BLANK); }, [open]);
  const updS = (i, k, v) => setF((p) => ({ ...p, shifts: p.shifts.map((s, j) => (j === i ? { ...s, [k]: v } : s)) }));
  const save = async () => {
    setBusy(true);
    try { onCreated((await api.post("/admin/cantiere-types", f)).data); toast.success("Tipo di cantiere creato"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="cantiere-type-dialog">
        <DialogHeader><DialogTitle className="font-display">Nuovo tipo di cantiere</DialogTitle></DialogHeader>
        <div className="space-y-1"><Label>Nome del tipo</Label><Input value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} placeholder="Es. Impianto fotovoltaico" data-testid="cantiere-type-name" /></div>
        <div className="flex gap-2">
          {[["flex", "Orario flessibile"], ["recur", "Ripetitivo settimanale"]].map(([k, l]) => (
            <button key={k} type="button" data-active={f.mode === k} onClick={() => setF((p) => ({ ...p, mode: k }))} className="choice flex-1 rounded-full border bg-white px-3 py-1.5 text-sm" data-testid={`cantiere-type-mode-${k}`}>{l}</button>
          ))}
        </div>
        {f.mode === "flex" ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label className="text-xs">Dalle</Label><Input type="time" value={f.start} onChange={(e) => setF((p) => ({ ...p, start: e.target.value }))} data-testid="cantiere-type-start" /></div>
            <div className="space-y-1"><Label className="text-xs">Alle</Label><Input type="time" value={f.end} onChange={(e) => setF((p) => ({ ...p, end: e.target.value }))} data-testid="cantiere-type-end" /></div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">{WD.map((d, i) => (
              <button key={d} type="button" data-active={f.weekdays.includes(i)} onClick={() => setF((p) => ({ ...p, weekdays: p.weekdays.includes(i) ? p.weekdays.filter((x) => x !== i) : [...p.weekdays, i].sort() }))}
                className="choice rounded-full border bg-white px-3 py-1 text-xs" data-testid={`cantiere-type-wd-${i}`}>{d}</button>))}</div>
            {f.shifts.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input type="time" value={s.start} onChange={(e) => updS(i, "start", e.target.value)} className="w-28" data-testid={`cantiere-type-shift-start-${i}`} />
                <span className="text-slate-400">–</span>
                <Input type="time" value={s.end} onChange={(e) => updS(i, "end", e.target.value)} className="w-28" data-testid={`cantiere-type-shift-end-${i}`} />
                {f.shifts.length > 1 && <Button type="button" size="icon" variant="ghost" onClick={() => setF((p) => ({ ...p, shifts: p.shifts.filter((_, j) => j !== i) }))}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
              </div>
            ))}
            {f.shifts.length < 4 && <Button type="button" size="sm" variant="ghost" className="-ml-2" onClick={() => setF((p) => ({ ...p, shifts: [...p.shifts, { start: "14:00", end: "18:00" }] }))} data-testid="cantiere-type-add-shift"><Plus className="h-4 w-4" /> Aggiungi fascia</Button>}
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={busy} className="rounded-full" data-testid="cantiere-type-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Crea tipo</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CantiereTypePicker({ value, onPick }) {
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/admin/cantiere-types").then((r) => setTypes(r.data)); }, []);
  const sel = types.find((t) => t.name === value);
  return (
    <div className="space-y-2 sm:col-span-2">
      <Label>Tipo di cantiere</Label>
      <div className="flex gap-2">
        <select value={value || ""} onChange={(e) => onPick(types.find((t) => t.name === e.target.value) || null)}
          className="flex-1 rounded-lg border bg-white px-3 py-2 text-sm" data-testid="cantiere-type-select">
          <option value="">Nessun tipo</option>
          {types.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
        </select>
        <Button type="button" size="icon" className="shrink-0 rounded-full" onClick={() => setOpen(true)} title="Nuovo tipo di cantiere" data-testid="cantiere-type-add-button"><Plus className="h-4 w-4" /></Button>
      </div>
      {sel && <p className="text-xs text-slate-500" data-testid="cantiere-type-summary">Orario {typeLabel(sel)}: applicato qui sotto, puoi modificarlo.</p>}
      <NewTypeDialog open={open} onClose={() => setOpen(false)} onCreated={(t) => { setTypes((p) => [...p, t].sort((a, b) => a.name.localeCompare(b.name))); setOpen(false); onPick(t); }} />
    </div>
  );
}
